'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/** Rosa y amarillo de firma del tema mercado (papel picado). */
const ROSA = '#e0447c'
const AMARILLO = '#f2b134'

/** Colores del papel picado en ciclo verde→rosa→amarillo. */
const PICADO = ['var(--ms-accent)', ROSA, AMARILLO]

/**
 * Franja de papel picado — firma del tema mercado: banda tricolor con
 * banderines triangulares colgando, en CSS plano (sin imágenes).
 */
function PapelPicado() {
  return (
    <div aria-hidden>
      <div className="flex h-[7px]">
        {Array.from({ length: 9 }, (_, i) => (
          <span key={i} className="flex-1" style={{ background: PICADO[i % 3] }} />
        ))}
      </div>
      <div className="flex justify-between px-1">
        {Array.from({ length: 18 }, (_, i) => (
          <span
            key={i}
            className="block w-0 h-0"
            style={{
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderTop: `7px solid ${PICADO[Math.floor(i / 2) % 3]}`,
            }}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Hero del tema "mercado" (herencia latina viva): fachada de taquería con
 * papel picado. Franja tricolor con banderines arriba, nombre en Shrikhand
 * (SOLO el nombre — regla del tema: todo lo demás va en body), chips vivos
 * verde/rosa y foto enmarcada con el filo tricolor. La barra superior usa
 * modo sólido (navSolid). Lee tokens del tema (--ms-*).
 */
export function HeroMercado({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* mt despeja la barra superior fija (navSolid); el papel picado abre la fachada */}
      <div className="mt-[68px] sm:mt-[72px]">
        <PapelPicado />
      </div>

      <div className="mx-auto max-w-4xl px-6 sm:px-8 pt-12 sm:pt-16 pb-12 text-center">
        {city && (
          <div
            className="text-[11.5px] font-bold uppercase tracking-[0.22em] mb-4"
            style={{ color: 'var(--ms-text-muted)' }}
          >
            {city}
          </div>
        )}

        <h1
          className="leading-[1.08] text-[clamp(38px,7.5vw,78px)]"
          style={{ fontFamily: 'var(--ms-font-display)', color: 'var(--ms-accent)' }}
        >
          {businessName}
        </h1>

        {tagline && (
          <p className="mt-4 max-w-xl mx-auto text-[16px] sm:text-[17px] leading-relaxed" style={{ color: 'var(--ms-text-soft)' }}>
            {tagline}
          </p>
        )}

        {/* Meta discreta */}
        <div
          className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[13px] font-medium"
          style={{ color: 'var(--ms-text-muted)' }}
        >
          {statusLabel && (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#2fa96d'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs: verde sólido + chip rosa */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center rounded-full px-8 py-3.5 text-[15px] font-bold transition hover:brightness-105 active:scale-[0.98]"
            style={{
              background: 'var(--ms-accent)',
              color: '#ffffff',
              boxShadow: '0 12px 26px -12px color-mix(in srgb, var(--ms-accent) 65%, transparent)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center rounded-full px-6 py-3.5 text-[14.5px] font-bold transition hover:brightness-95"
              style={{
                color: ROSA,
                border: `1.5px solid color-mix(in srgb, ${ROSA} 55%, transparent)`,
                background: 'var(--ms-surface)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>

      {/* Foto enmarcada con el filo tricolor arriba */}
      {hasMedia && (
        <div className="mx-auto max-w-5xl px-6 sm:px-8 pb-16">
          <div
            className="relative w-full overflow-hidden"
            style={{
              aspectRatio: '16 / 7',
              borderRadius: 'calc(var(--ms-radius) * 1.4)',
              border: '1px solid var(--ms-border)',
              boxShadow: 'var(--ms-card-shadow)',
            }}
          >
            <div className="absolute top-0 left-0 right-0 z-10 flex h-[6px]">
              {Array.from({ length: 9 }, (_, i) => (
                <span key={i} className="flex-1" style={{ background: PICADO[i % 3] }} />
              ))}
            </div>
            {heroImageUrl ? (
              <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
            ) : heroVideoUrl ? (
              <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
            ) : null}
          </div>
        </div>
      )}
    </section>
  )
}
